/**
 * All public page copy. The site speaks to foreign companies and individuals
 * entering or operating in Thailand. Office facts (address, phone) live in
 * src/config/contact.ts and are not repeated here.
 */

export const HERO = {
  eyebrow: 'Strategic Advisory · Government Relations',
  headline: 'Seamless Market Entry Into Thailand.',
  lead:
    'We help foreign companies and individuals navigate Thailand’s complex regulatory, political, and commercial landscape — from licensing and structuring to reputation, stakeholder engagement, and growth.',
  primary: 'Speak with Our Team',
  secondary: 'Explore Our Services',
} as const;

export const META = {
  homeTitle: 'Strategic advisory in Thailand',
  homeDescription: HERO.lead,
  contactDescription:
    'Contact Summerlin for a confidential discussion about market entry, expansion, or ongoing advisory in Thailand. Empire Tower, Bangkok.',
} as const;

/** The Group CEO's line, word for word from the original summerlinasia.com
 *  home page. Shown in Who we are. */
export const CEO_QUOTE = {
  quote: 'The real measure of success for any company should be the number of lives it has positively impacted',
  attribution: 'Group CEO, Summerlin Co., Ltd.',
} as const;

export const WHO_WE_ARE = [
  'We are a specialised advisory, public relations, and consultancy firm focused exclusively on foreign entities doing business in Thailand.',
  'We combine deep local insight with practical execution across technology, finance, business development, government relations, and conflict resolution. Our work spans both private and public sectors, helping clients establish, protect, and grow their interests in Thailand.',
  'Whether you are entering Thailand for the first time or already operating here, we provide the integrated support required to move with clarity and confidence.',
] as const;

/** Eyebrow + statement headline for each section. Titles reuse page copy. */
export const SECTION_HEADS = {
  whoWeAre: { eyebrow: 'Who we are', title: 'Deep local insight. Practical execution.' },
  services: { eyebrow: 'Our services', title: 'From licensing and structuring to reputation and growth.' },
  audiences: { eyebrow: 'Who we serve', title: 'Foreign entities entering or operating in Thailand.' },
  approach: { eyebrow: 'Our approach', title: 'Understand, structure, engage, sustain.' },
} as const;

export interface Audience {
  title: string;
  body: string;
}

export const AUDIENCES: Audience[] = [
  {
    title: 'Foreign companies and investors',
    body: 'Seeking market entry or expansion in Thailand.',
  },
  {
    title: 'Multinational and regional firms',
    body: 'Already operating in Thailand and in need of ongoing advisory, licensing, or stakeholder support.',
  },
  {
    title: 'Technology and finance organisations',
    body: 'Navigating regulatory and commercial complexity.',
  },
  {
    title: 'Executives and entrepreneurs',
    body: 'Requiring practical guidance on structure, compliance, and relationships.',
  },
];

/** Where clients come from, shown as a strip under the audience grid. */
export const AUDIENCE_ORIGINS = [
  'Europe',
  'North America',
  'China',
  'India',
  'the Middle East',
  'and beyond',
] as const;

export interface Service {
  slug: string;
  title: string;
  short: string;
  body: string;
  /** Short scan tags, lifted from `body` — no new claims. */
  scope: string[];
}

export const SERVICES: Service[] = [
  {
    slug: 'market-entry',
    title: 'Market Entry & Corporate Structuring',
    short: 'Market entry',
    body: 'Entity selection and incorporation, foreign ownership structuring, business licensing, permits, and fast-track support in Thailand. We handle the practical steps so you can focus on operations.',
    scope: ['Incorporation', 'Foreign ownership', 'Licensing & permits', 'Fast-track'],
  },
  {
    slug: 'government-relations',
    title: 'Government Relations & Public Affairs',
    short: 'Government relations',
    body: 'Stakeholder mapping, policy monitoring, regulatory engagement, and relationship management with government bodies and investment authorities. We help protect and advance your licence to operate.',
    scope: ['Stakeholder mapping', 'Policy monitoring', 'Regulatory engagement'],
  },
  {
    slug: 'strategic-advisory',
    title: 'Strategic Advisory & Business Development',
    short: 'Strategic advisory',
    body: 'Sector-focused guidance in technology and finance, commercial strategy, partner identification, and growth planning tailored to local market realities.',
    scope: ['Technology & finance', 'Commercial strategy', 'Partner identification'],
  },
  {
    slug: 'public-relations',
    title: 'Public Relations & Reputation Management',
    short: 'Public relations',
    body: 'Corporate communications, media relations, narrative development, and crisis support designed for Thailand’s media and stakeholder environment.',
    scope: ['Media relations', 'Narrative development', 'Crisis support'],
  },
  {
    slug: 'conflict-resolution',
    title: 'Conflict Resolution & Risk Management',
    short: 'Conflict resolution',
    body: 'Practical support in commercial disputes, stakeholder mediation, political and reputational risk mitigation, and issues management.',
    scope: ['Commercial disputes', 'Stakeholder mediation', 'Issues management'],
  },
];

export const SERVICES_INTRO =
  'Integrated support that connects strategy, relationships, communications, and execution.';

export const SERVICES_NOTE =
  'Services can be engaged modularly or as integrated retainers.';

/** A pull statement plus three pillars; every line is lifted from the brief. */
export const WHY = {
  statement: 'Thailand is its own market.',
  lead: 'Its regulatory framework, political dynamics, business culture, and approval processes do not follow a regional template.',
  pillars: [
    {
      title: 'Integrated',
      body: 'One firm across technology, finance, business development, government relations, and conflict resolution.',
    },
    {
      title: 'From insight to action',
      body: 'Securing licences, building the right relationships, managing reputation, and resolving issues before they escalate.',
    },
    {
      title: 'Pragmatic, discreet, results-oriented',
      body: 'Success requires more than generic advice.',
    },
  ],
} as const;

export interface Step {
  name: string;
  body: string;
}

export const APPROACH: Step[] = [
  {
    name: 'Understand',
    body: 'We begin with a clear assessment of your objectives, risk profile, and plans for Thailand.',
  },
  {
    name: 'Structure',
    body: 'We design the optimal legal, commercial, and stakeholder pathway.',
  },
  {
    name: 'Engage',
    body: 'We support licensing, government relations, communications, and on-the-ground execution.',
  },
  {
    name: 'Sustain',
    body: 'We provide ongoing advisory to protect and grow your position as Thai regulations evolve.',
  },
];

export const CTA = {
  title: 'Ready to move forward in Thailand?',
  lead: 'Contact us for a confidential discussion about your market entry, expansion, or ongoing advisory needs.',
} as const;

export const CONTACT_PAGE = {
  ...CTA,
  placeholder: 'Tell us about your market, timeline, and the support you need.',
} as const;

export const FOOTER = {
  summary:
    'Focused exclusively on foreign entities in Thailand. Integrated advisory, government relations, PR, and market-entry execution.',
  disciplines:
    'Technology · Finance · Business Development · Government Relations · Conflict Resolution',
} as const;
